import React from "react";
import { ArrowLeft, Shield, AlertTriangle, Scale, FileText } from "lucide-react";
import styles from "./TermsOfService.module.css";
import { useAppContext } from "../App";

export default function TermsOfService() {
  const { setActiveTab } = useAppContext();

  const handleBack = () => {
    setActiveTab("Instruction Page");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <main className={styles.main}>
      <div className={styles.container}>
        {/* Back Button */}
        <button className={styles.backButton} onClick={handleBack}>
          <ArrowLeft size={18} /> Back to Home
        </button>

        {/* Header */}
        <header className={styles.header}>
          <div className={styles.headerIcon}>
            <Scale size={32} />
          </div>
          <h1>Terms of Service</h1>
          <p className={styles.lastUpdated}>Last Updated: January 28, 2026</p>
        </header>

        {/* Important Notice */}
        <div className={styles.noticeBox}>
          <AlertTriangle size={20} />
          <div>
            <strong>Important Notice</strong>
            <p>
              Please read these Terms of Service carefully before using 4exAI. By accessing or using our platform, 
              you acknowledge that you have read, understood, and agree to be bound by these terms.
            </p>
          </div>
        </div>

        {/* Table of Contents */}
        <nav className={styles.toc}>
          <h2>Contents</h2>
          <ol>
            <li><a href="#acceptance">Acceptance of Terms</a></li>
            <li><a href="#description">Description of Service</a></li>
            <li><a href="#not-advice">Not Financial Advice</a></li>
            <li><a href="#risk">Risk Acknowledgment</a></li>
            <li><a href="#data">Data and Information</a></li>
            <li><a href="#eligibility">Eligibility</a></li>
            <li><a href="#prohibited">Prohibited Uses</a></li>
            <li><a href="#ip">Intellectual Property</a></li>
            <li><a href="#disclaimer">Disclaimer of Warranties</a></li>
            <li><a href="#liability">Limitation of Liability</a></li>
            <li><a href="#indemnification">Indemnification</a></li>
            <li><a href="#termination">Termination</a></li>
            <li><a href="#changes">Changes to Terms</a></li>
            <li><a href="#governing">Governing Law</a></li>
            <li><a href="#contact">Contact Information</a></li>
          </ol>
        </nav>

        {/* Sections */}
        <article className={styles.content}>
          
          <section id="acceptance" className={styles.section}>
            <h2><span className={styles.sectionNumber}>1.</span> Acceptance of Terms</h2>
            <p>
              By accessing or using 4exAI ("the Platform," "the Service," "we," "us," or "our"), you ("User," "you," or "your") 
              agree to be bound by these Terms of Service ("Terms"). If you do not agree to all of these Terms, you must not 
              access or use the Platform.
            </p>
            <p>
              These Terms constitute a legally binding agreement between you and 4exAI. Your continued use of the Platform 
              following any modifications to these Terms constitutes acceptance of those modifications.
            </p>
          </section>

          <section id="description" className={styles.section}>
            <h2><span className={styles.sectionNumber}>2.</span> Description of Service</h2>
            <p>
              4exAI is a <strong>decision support platform</strong> that provides:
            </p>
            <ul>
              <li>Visualization and analysis of publicly available Commitments of Traders (CFTC COT) data</li>
              <li>Intraday pattern detection tools for FX and equity markets</li>
              <li>Educational information about market structure and positioning</li>
              <li>Technical analysis overlays and charting tools</li>
            </ul>
            <p>
              The Platform is designed to assist traders in making their own informed decisions. It does not execute trades, 
              manage funds, or provide personalized financial advice.
            </p>
          </section>

          <section id="not-advice" className={styles.section}>
            <div className={styles.criticalSection}>
              <h2><span className={styles.sectionNumber}>3.</span> Not Financial Advice</h2>
              <div className={styles.criticalContent}>
                <Shield size={24} />
                <div>
                  <p>
                    <strong>THE PLATFORM DOES NOT PROVIDE FINANCIAL, INVESTMENT, TAX, OR LEGAL ADVICE.</strong>
                  </p>
                  <p>
                    All information, data, analysis, patterns, setups, and any other content provided through the Platform 
                    ("Content") is for <strong>informational and educational purposes only</strong>.
                  </p>
                  <ul>
                    <li>We do not recommend any specific securities, derivatives, or trading strategies</li>
                    <li>We do not provide personalized investment advice</li>
                    <li>We are not a registered investment advisor, broker-dealer, or financial planner</li>
                    <li>We do not manage client funds or execute trades on behalf of users</li>
                    <li>Pattern detection is not a prediction of future price movement</li>
                  </ul>
                  <p>
                    You should consult with qualified financial, legal, and tax professionals before making any 
                    investment or trading decisions. Any decision to buy, sell, or hold any financial instrument 
                    is solely your responsibility.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section id="risk" className={styles.section}>
            <div className={styles.criticalSection}>
              <h2><span className={styles.sectionNumber}>4.</span> Risk Acknowledgment</h2>
              <div className={styles.criticalContent}>
                <AlertTriangle size={24} />
                <div>
                  <p>
                    <strong>TRADING AND INVESTING INVOLVES SUBSTANTIAL RISK OF LOSS.</strong>
                  </p>
                  <p>By using the Platform, you acknowledge and agree that:</p>
                  <ul>
                    <li>Trading foreign exchange (FX), equities, futures, and other financial instruments carries a high level of risk</li>
                    <li>You may lose some or all of your invested capital</li>
                    <li>Past performance, historical patterns, and backtested results do not guarantee future results</li>
                    <li>The Platform's pattern detection does not predict future price movements</li>
                    <li>Market conditions can change rapidly and without warning</li>
                    <li>You should only trade with capital you can afford to lose</li>
                    <li>Leverage can magnify both gains and losses</li>
                  </ul>
                  <p>
                    <strong>You are solely responsible for all trading and investment decisions you make.</strong> 
                    We are not responsible for any losses incurred as a result of using the Platform.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section id="data" className={styles.section}>
            <h2><span className={styles.sectionNumber}>5.</span> Data and Information</h2>
            <h3>5.1 Data Sources</h3>
            <p>The Platform utilizes data from various sources, including:</p>
            <ul>
              <li><strong>CFTC Commitments of Traders (COT) Report:</strong> Published weekly (as-of Tuesday, released Friday)</li>
              <li><strong>Market Price Data:</strong> From third-party data providers, which may be delayed</li>
              <li><strong>Derived Calculations:</strong> Computed by our proprietary algorithms</li>
            </ul>
            
            <h3>5.2 Data Limitations</h3>
            <ul>
              <li>CFTC data is inherently delayed (published 3 days after the reporting date)</li>
              <li>Market data may be delayed by 15 minutes or more depending on the source</li>
              <li>Data may contain errors, omissions, or inaccuracies</li>
              <li>Historical data may be revised by the original data providers</li>
              <li>We do not guarantee the accuracy, completeness, or timeliness of any data</li>
            </ul>

            <h3>5.3 No Look-Ahead Bias</h3>
            <p>
              We make reasonable efforts to present data as it would have been known at the relevant point in time. 
              However, users should independently verify data timing for any trading or research purposes.
            </p>
          </section>

          <section id="eligibility" className={styles.section}>
            <h2><span className={styles.sectionNumber}>6.</span> Eligibility</h2>
            <p>To use the Platform, you must:</p>
            <ul>
              <li>Be at least 18 years of age (or the age of majority in your jurisdiction)</li>
              <li>Have the legal capacity to enter into binding agreements</li>
              <li>Not be prohibited from using such services under applicable laws</li>
              <li>Comply with all applicable local, state, national, and international laws and regulations</li>
            </ul>
            <p>
              The Platform is not intended for use in jurisdictions where such use would be prohibited or restricted. 
              You are responsible for determining whether your use complies with applicable laws.
            </p>
          </section>

          <section id="prohibited" className={styles.section}>
            <h2><span className={styles.sectionNumber}>7.</span> Prohibited Uses</h2>
            <p>You agree NOT to:</p>
            <ul>
              <li>Use the Platform for any unlawful purpose or in violation of any applicable laws</li>
              <li>Redistribute, resell, or commercially exploit the Platform's Content without authorization</li>
              <li>Attempt to reverse engineer, decompile, or extract source code from the Platform</li>
              <li>Use automated systems (bots, scrapers) to access the Platform without permission</li>
              <li>Interfere with or disrupt the Platform's infrastructure or security</li>
              <li>Misrepresent your identity or affiliation</li>
              <li>Use the Platform to provide investment advice to third parties</li>
              <li>Share account credentials with unauthorized parties</li>
            </ul>
          </section>

          <section id="ip" className={styles.section}>
            <h2><span className={styles.sectionNumber}>8.</span> Intellectual Property</h2>
            <p>
              All content, features, functionality, design, text, graphics, logos, and software on the Platform 
              are owned by 4exAI or its licensors and are protected by intellectual property laws.
            </p>
            <p>
              You are granted a limited, non-exclusive, non-transferable license to access and use the Platform 
              for personal, non-commercial purposes in accordance with these Terms.
            </p>
            <p>
              You may not copy, modify, distribute, sell, or lease any part of the Platform without our prior 
              written consent.
            </p>
          </section>

          <section id="disclaimer" className={styles.section}>
            <h2><span className={styles.sectionNumber}>9.</span> Disclaimer of Warranties</h2>
            <p>
              THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS 
              OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
            </p>
            <ul>
              <li>Implied warranties of merchantability</li>
              <li>Fitness for a particular purpose</li>
              <li>Non-infringement</li>
              <li>Accuracy or reliability of any Content</li>
              <li>Uninterrupted or error-free operation</li>
            </ul>
            <p>
              We do not warrant that the Platform will meet your requirements, that results obtained will be 
              accurate or reliable, or that any errors will be corrected.
            </p>
          </section>

          <section id="liability" className={styles.section}>
            <h2><span className={styles.sectionNumber}>10.</span> Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, DENGAI AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, 
              AND AFFILIATES SHALL NOT BE LIABLE FOR:
            </p>
            <ul>
              <li>Any indirect, incidental, special, consequential, or punitive damages</li>
              <li>Any loss of profits, revenue, data, or business opportunities</li>
              <li>Any trading losses or investment losses</li>
              <li>Any damages resulting from unauthorized access to your account</li>
              <li>Any damages arising from interruption, suspension, or termination of the Platform</li>
            </ul>
            <p>
              Our total liability for any claims arising from your use of the Platform shall not exceed the 
              amount you paid to us (if any) in the twelve (12) months preceding the claim.
            </p>
          </section>

          <section id="indemnification" className={styles.section}>
            <h2><span className={styles.sectionNumber}>11.</span> Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless 4exAI and its officers, directors, employees, 
              agents, and affiliates from and against any claims, damages, losses, liabilities, costs, and 
              expenses (including reasonable attorneys' fees) arising from:
            </p>
            <ul>
              <li>Your use of the Platform</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any third-party rights</li>
              <li>Your trading or investment decisions</li>
              <li>Any Content you submit or transmit through the Platform</li>
            </ul>
          </section>

          <section id="termination" className={styles.section}>
            <h2><span className={styles.sectionNumber}>12.</span> Termination</h2>
            <p>
              We reserve the right to suspend or terminate your access to the Platform at any time, with or 
              without cause, and with or without notice. Upon termination:
            </p>
            <ul>
              <li>Your right to use the Platform will immediately cease</li>
              <li>Provisions that by their nature should survive termination shall remain in effect</li>
              <li>We are not liable for any termination of your access</li>
            </ul>
          </section>

          <section id="changes" className={styles.section}>
            <h2><span className={styles.sectionNumber}>13.</span> Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. Changes will be effective immediately 
              upon posting to the Platform. Your continued use of the Platform after any changes constitutes 
              acceptance of the modified Terms.
            </p>
            <p>
              We encourage you to review these Terms periodically. The "Last Updated" date at the top of this 
              page indicates when these Terms were last revised.
            </p>
          </section>

          <section id="governing" className={styles.section}>
            <h2><span className={styles.sectionNumber}>14.</span> Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the jurisdiction 
              in which 4exAI operates, without regard to conflict of law principles.
            </p>
            <p>
              Any disputes arising from these Terms or your use of the Platform shall be resolved through 
              binding arbitration in accordance with applicable arbitration rules, unless prohibited by law.
            </p>
          </section>

          <section id="contact" className={styles.section}>
            <h2><span className={styles.sectionNumber}>15.</span> Contact Information</h2>
            <p>
              If you have any questions about these Terms of Service, please contact us at:
            </p>
            <div className={styles.contactBox}>
              <p><strong>4exAI Support</strong></p>
              <p>Email: legal@dengai.com</p>
            </div>
          </section>

        </article>

        {/* Final Acknowledgment */}
        <div className={styles.acknowledgment}>
          <FileText size={20} />
          <p>
            By using 4exAI, you acknowledge that you have read, understood, and agree to be bound by these 
            Terms of Service. If you do not agree, please discontinue use of the Platform immediately.
          </p>
        </div>

        {/* Back to Top */}
        <div className={styles.backToTop}>
          <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            Back to Top
          </button>
        </div>
      </div>
    </main>
  );
}
